import { DMMProductLineCampaign, DMMProductLineCampaignFlatten, ENTITY_STATUS } from 'src/models/DmmModel';
import { OptionDropdown } from 'src/models/ForecastModels';
import { getOnboardBusinessSegmentBucketName } from 'src/utils/xpt-s3-bucket-details';

/**
 * Transforms a list of campaign data from nested structure to a flattened format.
 *
 * This function performs three main operations:
 * 1. Flattens the nested campaign_dimensions array into direct properties
 * 2. Formats the status from uppercase to proper case (e.g., "ACTIVE" → "Active")
 * 3. Converts UTC timestamps to readable local datetime strings
 *
 * @param listProductLineCampaigns - Array of campaign objects with nested dimension data
 * @returns Array of flattened campaign objects with formatted dates and status
 */
export const parseCampaignData = (listProductLineCampaigns: DMMProductLineCampaign[]): DMMProductLineCampaignFlatten[] => {
  return listProductLineCampaigns.map(flattenAndFormatCampaign);
};

/**
 * Processes a single campaign object to flatten its structure and format its values.
 *
 * @param campaign - Individual campaign object to process
 * @returns Flattened and formatted campaign object
 */
const flattenAndFormatCampaign = (campaign: DMMProductLineCampaign): DMMProductLineCampaignFlatten => {
  const dimensionFields = flattenDimensions(campaign.campaign_dimensions);
  const { campaign_dimensions, ...baseFields } = campaign;

  return {
    ...baseFields,
    ...dimensionFields,
    campaign_status: campaign.is_active ? ENTITY_STATUS.ACTIVE : ENTITY_STATUS.INACTIVE,
    updated_at: campaign.updated_at,
    created_at: campaign.created_at
  } as DMMProductLineCampaignFlatten;
};

/**
 * Converts campaign dimensions array into a flat object with dimension keys and values.
 *
 * @param dimensions - Array of campaign dimension objects
 * @returns Object with dimension keys mapped to their values
 */
const flattenDimensions = (dimensions: DMMProductLineCampaign['campaign_dimensions']): Record<string, string> => {
  return dimensions.reduce<Record<string, string>>(
    (acc, { dimension_key, dimension_value }) => ({
      ...acc,
      [dimension_key]: dimension_value
    }),
    {}
  );
};

/**
 * Extracts distinct country codes from campaign data and formats them as dropdown options
 *
 * @param campaigns - Array of flattened campaign objects
 * @returns Array of dropdown options with country codes
 */
export const getDistinctCountryCodes = (campaigns: DMMProductLineCampaignFlatten[]): OptionDropdown[] => {
  const distinctCodes = [...new Set(campaigns.map((campaign) => campaign.country_code))].sort();
  return distinctCodes.map((country) => ({ label: country, value: country }));
};

/**
 * Generates text for multi-select constraints showing selection count
 *
 * @param selectedCount - Number of selected items
 * @param totalCount - Total number of available items
 * @returns Formatted constraint text
 */
export const getMultiSelectConstraintText = (selectedCount: number, totalCount: number): string => {
  return selectedCount > 0 ? `${selectedCount} of ${totalCount} selected` : `${totalCount} available`;
};

/**
 * Generates the S3 URI for reading DMM campaigns data.
 *
 * @param dataClassificationId - The data classification ID
 * @param dataClassificationShortDesc - The short description of the data classification
 * @returns The S3 URI string for reading campaign data
 */
export const campaignDataReadS3URI = (dataClassificationId: number, dataClassificationShortDesc: string): string => {
  const { bucketName } = getOnboardBusinessSegmentBucketName();
  return `s3://${bucketName}/${dataClassificationShortDesc}_${dataClassificationId}/campaigns/list_campaigns_details.json`;
};

/**
 * Generates the S3 URI path for writing DMM campaigns data.
 *
 * @param dataClassificationId - The data classification ID
 * @param dataClassificationShortDesc - The short description of the data classification
 * @returns The S3 URI path for writing campaign data
 */
export const campaignDataWriteS3URI = (dataClassificationId: number, dataClassificationShortDesc: string): string => {
  return `${dataClassificationShortDesc}_${dataClassificationId}/campaigns/list_campaigns_details.json`;
};

/**
 * Extracts distinct campaign names from campaign data and formats them as dropdown options
 *
 * @param campaigns - Array of flattened campaign objects
 * @returns Array of dropdown options with campaign names
 */
export const getDistinctCampaignNames = (campaigns: DMMProductLineCampaignFlatten[]): OptionDropdown[] => {
  const uniqueCampaignNames = [...new Set(campaigns.map((campaign) => campaign.product_line_campaign_name))].sort();
  return uniqueCampaignNames.map((name) => ({
    label: name,
    value: name
  }));
};
